import { AfterViewInit, Component, QueryList, ViewChildren } from '@angular/core';
import { CustomInputComponent } from '../../../shared-components/custom-input/custom-input.component';
import { TranslateService } from '../../../services/translate/translate.service';
import { ComponentType, FormValidationUtils } from '../../../utils/form-validation';
import { SmartFormBaseComponent } from '../../../utils/smart-form-base.component';

@Component({
	selector: 'app-inputs-validation',
	templateUrl: './inputs-validation.component.html',
	styleUrls: ['./inputs-validation.component.scss'],
	standalone: false,
})
export class InputsValidationComponent extends SmartFormBaseComponent implements AfterViewInit {
	@ViewChildren(CustomInputComponent) inputComponents!: QueryList<CustomInputComponent>;

	constructor(private translateService: TranslateService) {
		super();
	}

	ngAfterViewInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(
			this.fieldConfigs,
			this.translateService,
			this.inputComponents
		);
	}

	private get fieldConfigs() {
		return [
			{ name: 'username', type: ComponentType.INPUT, validationRules: this.usernameValidationRules },
			{ name: 'email', type: ComponentType.INPUT, validationRules: this.emailValidationRules },
			{ name: 'password', type: ComponentType.INPUT, validationRules: this.passwordValidationRules },
			{ name: 'phone', type: ComponentType.INPUT, validationRules: this.phoneValidationRules },
			{ name: 'creditCard', type: ComponentType.INPUT, validationRules: this.creditCardValidationRules },
			{ name: 'zipCode', type: ComponentType.INPUT, validationRules: this.zipCodeValidationRules },
		];
	}

	private get inputRules() {
		return CustomInputComponent.createRules(this.translateService);
	}

	// Username validation rules
	get usernameValidationRules() {
		return [
			// Require non-empty value
			this.inputRules.required(),

			// Minimum 3 characters
			this.inputRules.minLength(3),

			// Maximum 20 characters
			this.inputRules.maxLength(20),

			// Custom rule: username cannot contain spaces
			this.inputRules.custom((value) => !value.includes(' '), 'Username cannot contain spaces', 'no-spaces'),

			// Custom rule: username must start with a letter
			this.inputRules.custom((value) => /^[a-zA-Z]/.test(value), 'Username must start with a letter', 'start-with-letter'),
		];
	}

	// Email validation rules
	get emailValidationRules() {
		return [
			// Require non-empty value
			this.inputRules.required(),

			// Email format validation
			this.inputRules.email(),

			// Custom rule: must be a business email (not personal domains)
			this.inputRules.custom(
				(value) => {
					if (!value) return true;
					const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
					const domain = value.split('@')[1];
					return !personalDomains.includes(domain);
				},
				'Please use a business email address',
				'business-email'
			),
		];
	}

	// Password validation rules with specific requirements
	get passwordValidationRules() {
		return [
			// Require non-empty value
			this.inputRules.required(),

			// At least 8 characters
			this.inputRules.minLength(8),

			// At least one digit
			this.inputRules.custom((value) => /\d/.test(value), 'Password must contain at least one digit', 'has-digit'),

			// At least one uppercase letter
			this.inputRules.custom((value) => /[A-Z]/.test(value), 'Password must contain at least one uppercase letter', 'has-uppercase'),

			// At least one lowercase letter
			this.inputRules.custom((value) => /[a-z]/.test(value), 'Password must contain at least one lowercase letter', 'has-lowercase'),

			// At least one special character from $%&#
			this.inputRules.custom(
				(value) => /[$%&#]/.test(value),
				'Password must contain at least one special character ($%&#)',
				'has-special-char'
			),
		];
	}

	// Phone number validation rules
	get phoneValidationRules() {
		return [
			// Require non-empty value
			this.inputRules.required(),

			// Must be exactly 10 digits
			this.inputRules.custom(
				(value) => /^\d{10}$/.test(value.replace(/\D/g, '')),
				'Phone number must be exactly 10 digits',
				'exact-10-digits'
			),

			// Must start with valid area code
			this.inputRules.custom(
				(value) => {
					const digits = value.replace(/\D/g, '');
					if (digits.length < 3) return true; // Let other validations handle length
					const areaCode = digits.substring(0, 3);
					// Valid area codes: 200-999 (excluding some reserved ranges)
					return /^[2-9][0-9][0-9]$/.test(areaCode);
				},
				'Phone number must have a valid area code',
				'valid-area-code'
			),
		];
	}

	// Credit card validation rules
	get creditCardValidationRules() {
		return [
			// Require non-empty value
			this.inputRules.required(),

			// Must be 13-19 digits
			this.inputRules.custom(
				(value) => {
					const digits = value.replace(/\D/g, '');
					return digits.length >= 13 && digits.length <= 19;
				},
				'Credit card number must be 13-19 digits',
				'card-length'
			),

			// Must pass Luhn algorithm (basic check)
			this.inputRules.custom(
				(value) => {
					const digits = value.replace(/\D/g, '');
					if (digits.length < 13) return false;

					// Luhn algorithm
					let sum = 0;
					let isEven = false;

					for (let i = digits.length - 1; i >= 0; i--) {
						let digit = parseInt(digits[i]);

						if (isEven) {
							digit *= 2;
							if (digit > 9) {
								digit -= 9;
							}
						}

						sum += digit;
						isEven = !isEven;
					}

					return sum % 10 === 0;
				},
				'Invalid credit card number',
				'luhn-check'
			),
		];
	}

	// ZIP code validation rules
	get zipCodeValidationRules() {
		return [
			// Require non-empty value
			this.inputRules.required(),

			// Must be 5 digits (US ZIP code)
			this.inputRules.custom((value) => /^\d{5}$/.test(value), 'ZIP code must be exactly 5 digits', 'zip-format'),
		];
	}

	// Convenience getters for template access
	get usernameValue(): string {
		return this.formFields.username?.value || '';
	}
	get emailValue(): string {
		return this.formFields.email?.value || '';
	}
	get passwordValue(): string {
		return this.formFields.password?.value || '';
	}
	get phoneValue(): string {
		return this.formFields.phone?.value || '';
	}
	get creditCardValue(): string {
		return this.formFields.creditCard?.value || '';
	}
	get zipCodeValue(): string {
		return this.formFields.zipCode?.value || '';
	}

	// Utility methods for form management
	getAllValidationErrors(): { [key: string]: string[] } {
		return FormValidationUtils.getAllValidationErrors(this.formFields);
	}

	override onSubmit(event?: Event): void {
		super.onSubmit(event);

		if (this.isFormValid) {
			const formData = this.formManager?.getValues() ?? {};
			console.log('Form submitted successfully:', formData);
			alert('Form submitted successfully! Check console for data.');
		} else {
			const errors = FormValidationUtils.getAllValidationErrors(this.formFields);
			console.log('Form validation failed:', errors);
			alert('Form validation failed. Check console for details.');
		}
	}
}
